import { useNode, useEditor } from "@craftjs/core";
import React, { useRef, useState, useMemo, useCallback, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import { useResponsive } from "../../hooks/useResponsive";
import { selectedOutlineSx, labelStyle, addButtonStyle, removeButtonStyle, inputStyle } from "../../config/settingsStyles";
import {
  ColorField,
  SliderField,
  SettingsSection,
} from "../ui/form/SettingsField";

// =============================================================================
// Types
// =============================================================================

export interface FilterableFeaturesProps {
  categories: string[];
  activeTabColor: string;
  categoryColors: Record<string, string>;
  cardsPerRow: number;
  backgroundColor: string;
  textColor: string;
  gap: number;
  paddingTop: number;
  maxWidth: number | string;
  paddingX: number;
  paddingBottom: number;
  width?: string;
  children?: React.ReactNode;
}

// =============================================================================
// Nav Arrow Button (top-right, inline with tabs)
// =============================================================================

const NavArrowButton = ({
  direction,
  onClick,
  disabled,
}: {
  direction: "left" | "right";
  onClick: (e: React.MouseEvent) => void;
  disabled: boolean;
}) => (
  <Box
    component="button"
    onClick={onClick}
    sx={{
      width: 32,
      height: 32,
      borderRadius: "50%",
      border: "1px solid",
      borderColor: disabled ? "#e5e7eb" : "#d1d5db",
      bgcolor: "#ffffff",
      color: disabled ? "#d1d5db" : "#6b7280",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: disabled ? "default" : "pointer",
      opacity: disabled ? 0.5 : 1,
      flexShrink: 0,
      outline: "none",
      "&:hover": disabled ? {} : { borderColor: "#9ca3af", color: "#374151" },
    }}
  >
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
      <path
        d={direction === "left" ? "M10 12L6 8L10 4" : "M6 4L10 8L6 12"}
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </Box>
);

// =============================================================================
// Main Component (isCanvas = true — children are independent CraftJS nodes)
// =============================================================================

export const FilterableFeatures = ({
  categories = [],
  activeTabColor = "#E8834A",
  categoryColors = {},
  cardsPerRow = 3,
  backgroundColor = "#ffffff",
  textColor = "#2C2C2B",
  gap = 24,
  paddingTop = 32,
  maxWidth = 1280,
  paddingX = 24,
  paddingBottom = 32,
  width,
  children,
}: FilterableFeaturesProps) => {
  const {
    id,
    connectors: { connect, drag },
    selected,
    actions: { setProp },
  } = useNode((state) => ({
    selected: state.events.selected,
  }));

  const { enabled } = useEditor((state) => ({
    enabled: state.options.enabled,
  }));

  const { childNodeIds, childCategories } = useEditor((state) => {
    const node = state.nodes[id];
    const ids = node?.data?.nodes || [];
    const cats: Record<string, string> = {};
    ids.forEach((childId) => {
      const childNode = state.nodes[childId];
      if (childNode?.data?.props?.category) {
        cats[childId] = childNode.data.props.category as string;
      }
    });
    return { childNodeIds: ids, childCategories: cats };
  });

  const containerRef = useRef<HTMLDivElement>(null);
  const { isMobile, isTablet } = useResponsive(containerRef as React.RefObject<HTMLElement>);

  const [activeCategory, setActiveCategory] = useState<string>("All");
  const [currentPage, setCurrentPage] = useState(0);

  // Tab reorder via mouse events (avoids CraftJS drag interference)
  const [dragTabIdx, setDragTabIdx] = useState<number | null>(null);
  const [dragOverTabIdx, setDragOverTabIdx] = useState<number | null>(null);
  const tabRefs = useRef<(HTMLElement | null)[]>([]);
  const dragFromRef = useRef<number | null>(null);
  const dragToRef = useRef<number | null>(null);

  const handleTabMouseDown = useCallback((e: React.MouseEvent, idx: number) => {
    if (!enabled) return;
    e.stopPropagation();
    e.preventDefault();
    setDragTabIdx(idx);
    dragFromRef.current = idx;
    dragToRef.current = null;

    const onMouseMove = (ev: MouseEvent) => {
      for (let i = 0; i < tabRefs.current.length; i++) {
        const el = tabRefs.current[i];
        if (el) {
          const rect = el.getBoundingClientRect();
          if (ev.clientX >= rect.left && ev.clientX <= rect.right && ev.clientY >= rect.top && ev.clientY <= rect.bottom) {
            dragToRef.current = i;
            setDragOverTabIdx(i);
            break;
          }
        }
      }
    };

    const onMouseUp = () => {
      const from = dragFromRef.current;
      const to = dragToRef.current;
      if (from !== null && to !== null && from !== to) {
        setProp((props: FilterableFeaturesProps) => {
          const cats = [...props.categories];
          const [moved] = cats.splice(from, 1);
          // After removing at `from`, indices >= from shift down by 1.
          // The green line shows BEFORE the tab at `to`, so when from < to
          // the real insert position in the shortened array is `to - 1`.
          const insertIdx = from < to ? to - 1 : to;
          cats.splice(insertIdx, 0, moved);
          props.categories = cats;
        });
      }
      dragFromRef.current = null;
      dragToRef.current = null;
      setDragTabIdx(null);
      setDragOverTabIdx(null);
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  }, [enabled, setProp]);

  const columns = isMobile ? 1 : isTablet ? 2 : cardsPerRow;

  const getCategoryColor = (category: string): string => {
    return categoryColors[category] || activeTabColor;
  };

  // Derive unique categories from children if no explicit categories set
  const derivedCategories = useMemo(() => {
    if (categories.length > 0) return categories;
    const cats = new Set<string>();
    Object.values(childCategories).forEach((cat) => {
      if (cat) cats.add(cat);
    });
    return Array.from(cats);
  }, [categories, childCategories]);

  // Filtered child indices (by category)
  const filteredIndices = useMemo(() => {
    return childNodeIds.reduce<number[]>((acc, childId, i) => {
      if (activeCategory === "All") {
        acc.push(i);
      } else {
        const cat = childCategories[childId];
        if (cat === activeCategory) acc.push(i);
      }
      return acc;
    }, []);
  }, [activeCategory, childNodeIds, childCategories]);

  // Pagination — show 2 rows at a time
  const rowsPerPage = 2;
  const itemsPerPage = columns * rowsPerPage;
  const totalPages = Math.ceil(filteredIndices.length / itemsPerPage);
  const needsPagination = filteredIndices.length > itemsPerPage;

  // Reset page when category changes or page is out of range
  const safePage = Math.min(currentPage, Math.max(0, totalPages - 1));
  if (safePage !== currentPage) setCurrentPage(safePage);

  const visibleIndices = useMemo(() => {
    return new Set(filteredIndices.slice(safePage * itemsPerPage, (safePage + 1) * itemsPerPage));
  }, [filteredIndices, safePage, itemsPerPage]);

  // In editor mode, show all children (so they can be dragged/edited)
  const showAll = enabled;

  const hasPrev = safePage > 0;
  const hasNext = safePage < totalPages - 1;

  // DOM-based visibility (more reliable than CSS nth-child with CraftJS)
  const gridRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!gridRef.current) return;
    const children = gridRef.current.children;
    for (let i = 0; i < children.length; i++) {
      (children[i] as HTMLElement).style.display = showAll || visibleIndices.has(i) ? "" : "none";
    }
  }, [showAll, visibleIndices, childNodeIds.length]);

  return (
    <Box
      ref={(ref: HTMLDivElement | null) => {
        if (ref) {
          connect(drag(ref));
          (containerRef as React.MutableRefObject<HTMLDivElement | null>).current = ref;
        }
      }}
      sx={{
        bgcolor: backgroundColor,
        maxWidth: isMobile ? "100%" : (typeof maxWidth === "string" ? maxWidth : (maxWidth ? `${maxWidth}px` : undefined)),
        width: "100%",
        mx: "auto",
        px: `${paddingX}px`,
        pt: `${paddingTop}px`,
        pb: `${paddingBottom}px`,
        ...(selected && selectedOutlineSx),
      }}
    >
      {/* ---- Tabs Row + Prev/Next ---- */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 3, gap: 1 }}>
        {/* Tabs */}
        <Box
          sx={{
            display: "flex",
            gap: 1,
            flex: 1,
            overflowX: "auto",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            "&::-webkit-scrollbar": { display: "none" },
            flexWrap: "nowrap",
          }}
        >
          <Box
            component="button"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              setActiveCategory("All");
              setCurrentPage(0);
            }}
            sx={{
              px: 2,
              py: 0.75,
              borderRadius: "999px",
              border: activeCategory === "All" ? "none" : "1px solid",
              borderColor: activeCategory === "All" ? "transparent" : "#d1d5db",
              bgcolor: activeCategory === "All" ? activeTabColor : "#ffffff",
              color: activeCategory === "All" ? "#ffffff" : textColor,
              fontSize: "0.875rem",
              fontWeight: 600,
              cursor: "pointer",
              outline: "none",
              whiteSpace: "nowrap",
              flexShrink: 0,
              "&:hover": { opacity: 0.85 },
            }}
          >
            All
          </Box>

          {derivedCategories.map((cat, idx) => {
            const isActive = activeCategory === cat;
            const catColor = getCategoryColor(cat);
            const isDragging = dragTabIdx === idx;
            const isDragOver = dragOverTabIdx === idx;
            return (
              <Box
                key={cat}
                component="button"
                ref={(el: HTMLDivElement | null) => { tabRefs.current[idx] = el; }}
                onMouseDown={(e: React.MouseEvent) => handleTabMouseDown(e, idx)}
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  if (dragTabIdx === null) {
                    setActiveCategory(cat);
                    setCurrentPage(0);
                  }
                }}
                sx={{
                  px: 2,
                  py: 0.75,
                  borderRadius: "999px",
                  border: isActive ? "none" : "1px solid",
                  borderColor: isActive ? "transparent" : "#d1d5db",
                  position: "relative",
                  ...(isDragOver && dragTabIdx !== null && dragTabIdx !== idx && {
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      left: -5,
                      top: 0,
                      bottom: 0,
                      width: "3px",
                      bgcolor: "#4caf50",
                      borderRadius: "2px",
                    },
                  }),
                  bgcolor: isActive ? catColor : "#ffffff",
                  color: isActive ? "#ffffff" : textColor,
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  cursor: enabled ? "grab" : "pointer",
                  outline: "none",
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                  opacity: isDragging ? 0.4 : 1,
                  transition: "all 0.15s",
                  "&:hover": { opacity: isDragging ? 0.4 : 0.85 },
                }}
              >
                {cat}
              </Box>
            );
          })}
        </Box>

        {/* Prev/Next arrows — only when cards exceed 2 rows */}
        {!showAll && needsPagination && (
          <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
            <NavArrowButton
              direction="left"
              disabled={!hasPrev}
              onClick={(e) => {
                e.stopPropagation();
                if (hasPrev) setCurrentPage((p) => p - 1);
              }}
            />
            <NavArrowButton
              direction="right"
              disabled={!hasNext}
              onClick={(e) => {
                e.stopPropagation();
                if (hasNext) setCurrentPage((p) => p + 1);
              }}
            />
          </Box>
        )}
      </Box>

      {/* ---- Cards Grid ---- */}
      <Box
        ref={gridRef}
        sx={{
          display: "grid",
          gridTemplateColumns: `repeat(${columns}, 1fr)`,
          gap: `${isMobile ? Math.max(12, gap * 0.5) : gap}px`,
        }}
      >
        {children}
      </Box>

      {/* Empty state when no children */}
      {childNodeIds.length === 0 && (
        <Box sx={{ py: 6, textAlign: "center", color: textColor, opacity: 0.5 }}>
          <Typography sx={{ fontSize: "0.875rem" }}>
            Drop FeatureCard components here
          </Typography>
        </Box>
      )}
    </Box>
  );
};

// =============================================================================
// Settings Component
// =============================================================================

const FilterableFeaturesSettings = () => {
  const {
    actions: { setProp },
    categories,
    activeTabColor,
    categoryColors,
    cardsPerRow,
    backgroundColor,
    textColor,
    gap,
    paddingTop,
    maxWidth,
    paddingX,
    paddingBottom,
  } = useNode((node) => ({
    categories: node.data.props.categories as string[],
    activeTabColor: node.data.props.activeTabColor as string,
    categoryColors: node.data.props.categoryColors as Record<string, string>,
    cardsPerRow: node.data.props.cardsPerRow as number,
    backgroundColor: node.data.props.backgroundColor as string,
    textColor: node.data.props.textColor as string,
    gap: node.data.props.gap as number,
    paddingTop: node.data.props.paddingTop as number,
    maxWidth: node.data.props.maxWidth as number,
    paddingX: node.data.props.paddingX as number,
    paddingBottom: node.data.props.paddingBottom as number,
  }));

  return (
    <SettingsSection>
      {/* ---- Categories ---- */}
      <div>
        <label style={labelStyle}>Categories</label>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {categories.map((cat, index) => (
            <Box key={index} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <input
                type="text"
                value={cat}
                onChange={(e) =>
                  setProp((props: FilterableFeaturesProps) => {
                    props.categories = props.categories.map((c, i) => (i === index ? e.target.value : c));
                  })
                }
                style={{ ...inputStyle, flex: 1 }}
              />
              <button
                onClick={() =>
                  setProp((props: FilterableFeaturesProps) => {
                    props.categories = props.categories.filter((_, i) => i !== index);
                  })
                }
                style={removeButtonStyle}
              >
                ×
              </button>
            </Box>
          ))}
        </Box>
        <button
          onClick={() =>
            setProp((props: FilterableFeaturesProps) => {
              props.categories = [...props.categories, "New Category"];
            })
          }
          style={addButtonStyle}
        >
          + Add Category
        </button>
      </div>

      {/* ---- Active Tab Color ---- */}
      <ColorField
        label="Active Tab Color"
        value={activeTabColor}
        onChange={(v) => setProp((props: FilterableFeaturesProps) => (props.activeTabColor = v))}
      />

      {/* ---- Category Colors ---- */}
      {categories.length > 0 && (
        <div>
          <label style={labelStyle}>Category Colors</label>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {categories.map((cat) => (
              <Box key={cat} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <Typography variant="caption" sx={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {cat}
                </Typography>
                <input
                  type="color"
                  value={categoryColors[cat] || activeTabColor}
                  onChange={(e) =>
                    setProp((props: FilterableFeaturesProps) => {
                      props.categoryColors = { ...props.categoryColors, [cat]: e.target.value };
                    })
                  }
                  style={{ width: 40, height: 28, border: "1px solid #d1d5db", borderRadius: 4, cursor: "pointer", padding: 0 }}
                />
              </Box>
            ))}
          </Box>
        </div>
      )}

      {/* ---- Layout Settings ---- */}
      <SliderField
        label="Cards Per Row"
        value={cardsPerRow}
        onChange={(v) => setProp((props: FilterableFeaturesProps) => (props.cardsPerRow = v))}
        min={1}
        max={6}
      />

      <SliderField
        label="Padding Top"
        value={paddingTop}
        onChange={(v) => setProp((props: FilterableFeaturesProps) => (props.paddingTop = v))}
        min={0}
        max={64}
        unit="px"
      />

      <SliderField
        label="Padding Bottom"
        value={paddingBottom}
        onChange={(v) => setProp((props: FilterableFeaturesProps) => (props.paddingBottom = v))}
        min={0}
        max={64}
        unit="px"
      />

      <SliderField
        label="Padding X"
        value={paddingX}
        onChange={(v) => setProp((props: FilterableFeaturesProps) => (props.paddingX = v))}
        min={0}
        max={120}
        unit="px"
      />

      <SliderField
        label="Max Width"
        value={maxWidth}
        onChange={(v) => setProp((props: FilterableFeaturesProps) => (props.maxWidth = v))}
        min={0}
        max={1600}
        unit="px"
      />

      <SliderField
        label="Gap"
        value={gap}
        onChange={(v) => setProp((props: FilterableFeaturesProps) => (props.gap = v))}
        min={8}
        max={48}
        unit="px"
      />

      {/* ---- Color Settings ---- */}
      <ColorField
        label="Background Color"
        value={backgroundColor}
        onChange={(v) => setProp((props: FilterableFeaturesProps) => (props.backgroundColor = v))}
      />

      <ColorField
        label="Text Color"
        value={textColor}
        onChange={(v) => setProp((props: FilterableFeaturesProps) => (props.textColor = v))}
      />
    </SettingsSection>
  );
};

// =============================================================================
// Craft Config
// =============================================================================

FilterableFeatures.craft = {
  displayName: "Feature Grid",
  isCanvas: true,
  props: {
    categories: ["Category A", "Category B", "Category C"],
    activeTabColor: "#E8834A",
    categoryColors: {},
    cardsPerRow: 3,
    backgroundColor: "#ffffff",
    textColor: "#2C2C2B",
    gap: 24,
    paddingTop: 32,
    maxWidth: 1280,
    paddingX: 24,
    paddingBottom: 32,
    width: undefined,
  },
  rules: {
    canDrag: () => true,
    canMoveIn: () => true,
  },
  related: {
    settings: FilterableFeaturesSettings,
  },
};
